import axios, { CancelTokenSource } from 'axios';
import PatientDocumentComments, {
  PatientMedicalDocumentCommentDtoExtended,
  prepareComments,
  prepareUpsertCommentsDtos
} from 'components/clinical-data/PatientDocumentComments';
import ImageComponent from 'components/image/ImageComponent';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import _ from 'lodash';
import React, { useEffect, useState } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Icon, List, Modal, Segment, SemanticICONS } from "semantic-ui-react";
import {
  getMediaDocumentPreviewUrl,
  setPatientDocumentSetPatientDocumentTextContent,
  setPatientSynopsisReportControlledStatus
} from 'services/patientRecordServices';
import styled from "styled-components";
import { CdrDocumentFileContent, PatientMedicalDocumentCommentDto, UserDisplayDto } from "ts-types/api.types";
import { errorUtils } from 'utils/errorUtils';

const StyledModal = styled(Modal)`
  height: 90vh;
  width: 90vw !important;
`;

const StyledModalContent = styled(Modal.Content)`
  padding: 0 !important;
  height: 100% !important;

  .ui.inline.loader.active.send-email-in-progress {
    margin-top: -0.25rem;
    margin-left: 0.375rem;
  }
`;

const StyledSegmentGroup = styled(Segment.Group)`
  margin: 0 !important;
  height: 100% !important;
  border: none !important;
`;

const LeftSegment = styled(Segment)`
  width: 70% !important;
  padding: 0 !important;

  .audio {
    width: 100%;
    max-width: 470px;
    margin: 1rem;
  }

  .video {
    position: absolute;
    padding: 0.7rem;
    width: 100%;
    height: 100%;
  }
;
`;

const StyledButton = styled(Button)`
  &.ui.inverted.button.styled-button {
    box-shadow: none !important;
    opacity: 0.7;
  }

  &.ui.inverted.button:hover,
  &.ui.inverted.button:active,
  &.ui.inverted.button:focus {
    opacity: 1;
    color: #FFFFFF !important;
    background-color: transparent !important;
  }
`;

const RightSegment = styled(Segment.Group)`
  display: flex;
  flex-direction: column;
  width: 30% !important;
  margin: 0 !important;
  border: none !important;
  overflow: hidden;

  .segment-body {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    overflow: auto;

    label {
      font-weight: bold;
    }

    .form-container {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
      padding: 0 0.75rem;
      overflow: visible;
    }

    .form-container > .field {
      height: 100%;
    }

    .form-container .ui.form.textarea {
      height: 100%;

      textarea {
        height: 100%;
      }
    }
  }

  .actions-row {
    flex: 0 0 auto;
    display: flex;
    border-top: 1px solid rgba(34, 36, 38, .15);
    padding: 0.75rem 0.5rem;
    align-items: center;

    button {
      margin-left: 0.5rem;
    }

    .divider {
      flex: 1 1 auto;
    }
  }
`;

const ListContent = styled.div`
  width: 90%;
  display: inline-block;
  align-items: center;
`;

const DownloadSection = styled.div`
  width: 10%;
  display: inline-block;
  align-items: center;
  vertical-align: top;
`;

const StyledListItem = styled(List.Item)`
  padding: 0.75rem !important;
`;

const btnStyle = {
  background: "transparent",
  cursor: "pointer",
  padding: "5px 10px"
};

enum FileTypes {
  PDF,
  IMAGE,
  AUDIO,
  VIDEO,
  OTHER
}

interface Props extends MessageSourceProps {
  pdfResponses: CdrDocumentFileContent[],
  modalOpen: boolean,
  onCloseModal: () => void,
  getPdfUrl: (documentResponse: CdrDocumentFileContent) => string,

  textContent: string,
  comments: PatientMedicalDocumentCommentDto[],
  currentUserDisplay: UserDisplayDto,
  patientId: number,
  documentId: number,
  showSetControlledButton: boolean,
  successfullyChangedReportStatus: () => void,
  draftMode: boolean
}

const PatientMedicalDocumentPdfModal = (props: Props) => {

  const {
    pdfResponses,
    modalOpen,
    onCloseModal,
    getMessage,
    getPdfUrl,
    textContent,
    patientId,
    documentId,
    showSetControlledButton,
    draftMode,
    successfullyChangedReportStatus
  } = props;

  if (!pdfResponses || pdfResponses.length == 0) {
    onCloseModal();
  }

  const cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  const [selectedPdfResponse, setSelectedPdfResponse] =
      useState<CdrDocumentFileContent>(pdfResponses[0]);

  const [addingComment, setAddingComment] = useState(false);
  const [editingComments, setEditingComments] = useState(false);

  const [comments, setComments] =
      useState<Partial<PatientMedicalDocumentCommentDtoExtended>[]>([]);

  const [photoIndex, setPhotoIndex] = useState(0 as number);

  const [errorMessage, setErrorMessage] =
      useState<string | undefined>(undefined);

  const [segmentWidth, setSegmentWidth] = useState(0 as number);
  const [segmentHeight, setSegmentHeight] = useState(0 as number);

  useEffect(() => {
    const docComments = props.comments;
    if (docComments) {
      let comments = prepareComments(docComments);
      setComments(comments);
    }

    const height = document.getElementById("leftSegment")!.clientHeight;
    const width = document.getElementById("leftSegment")!.clientWidth;

    setSegmentWidth(width);
    setSegmentHeight(height);

    setPhotoIndex(0)
  }, [selectedPdfResponse]);

  const savePatientMedicalDocument = async () => {

    const upsertComments = prepareUpsertCommentsDtos(comments);

    await setPatientDocumentSetPatientDocumentTextContent(
        patientId, documentId, upsertComments, cancelTokenSource);

    onCloseModal();
  }

  const handleError = (error: any) => {

    const errorCode = error.errorCode;
    const knownErrors: Array<String> = [
      errorUtils.canNotSetControlledStatusOnWrongDocumentType
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        setErrorMessage(getMessage('error.general'));
      }
    }
  };

  const setControlledStatus = async (documentId: number, patientId: number) => {

    setPatientSynopsisReportControlledStatus(documentId, patientId, textContent, cancelTokenSource)
        .then(response => {
          successfullyChangedReportStatus();
          onCloseModal();
        })
        .catch((error: any) => handleError(error.response.data));
  }


  const contentType = selectedPdfResponse.contentType;

  const getType = (contentType: string): FileTypes => {
    let type: FileTypes = FileTypes.OTHER;
    if (contentType === "application/pdf") {
      type = FileTypes.PDF;
    } else if (contentType.includes("audio")) {
      type = FileTypes.AUDIO;
    } else if (contentType.includes("video")) {
      type = FileTypes.VIDEO;
    }
    return type;
  }
  const type = getType(contentType);

  const fileDownloadUrl: string = getPdfUrl(selectedPdfResponse);

  let filePreviewUrl: string = fileDownloadUrl;
  if (contentType.includes("audio")
      || contentType.includes("video")) {
    filePreviewUrl = getMediaDocumentPreviewUrl(selectedPdfResponse);
  }


  const imageParts = selectedPdfResponse.images;

  const mainImageSrc = contentType === "image/tiff"
      ? `data:${contentType};base64, ${imageParts[photoIndex]}`
      : `data:${contentType};base64, ${imageParts[0]}`;

  const imageTitle = () => {
    return contentType === "image/tiff"
        ? <div>
            <span>
              {selectedPdfResponse.pdfName}
            </span>
          <span style={{marginLeft: "5rem"}}>
              {photoIndex + 1}/{imageParts.length}
            </span>
        </div>
        : <span style={{textAlign: "center"}}>
          {selectedPdfResponse.pdfName}
        </span>
  }

  const prevSrc = () => {
    let prevSrc = undefined;

    if (contentType === "image/tiff") {
      photoIndex === 0
          ? prevSrc = `data:${contentType};base64, ${imageParts[0]}`
          : prevSrc = `data:${contentType};base64, ${imageParts[photoIndex - 1]}`
    }

    return prevSrc;
  }

  const movePrevRequest = () => {

    if (contentType === "image/tiff") {
      if (photoIndex == 0) {
        setPhotoIndex(0)
      } else {
        setPhotoIndex(photoIndex - 1)
      }
    }
  }

  const nextSrc = () => {
    let nextSrc = undefined;

    if (contentType === "image/tiff") {
      if (photoIndex === imageParts.length - 1) {
        nextSrc = `data:${contentType};base64, ${imageParts[photoIndex]}`
      } else {
        nextSrc = `data:${contentType};base64, ${imageParts[photoIndex + 1]}`
      }
    }

    return nextSrc;
  }

  const moveNextRequest = () => {

    if (contentType === "image/tiff") {
      if (photoIndex === imageParts.length - 1) {
        setPhotoIndex(imageParts.length - 1);
      } else {
        setPhotoIndex(photoIndex + 1)
      }
    }
  }

  const getIcon = (contentType: string): SemanticICONS => {
    let icon: SemanticICONS = "file pdf outline";
    const type = getType(contentType);
    if (type === FileTypes.OTHER) {
      icon = "file image outline"
    } else if (type === FileTypes.AUDIO) {
      icon = "file audio outline"
    } else if (type === FileTypes.VIDEO) {
      icon = "file video outline"
    }
    return icon;
  }

  const mappedPdfResponses = pdfResponses.map((pdfResponse, index) => (
      <StyledListItem
          key={index}
          active={selectedPdfResponse.pdfId == pdfResponse.pdfId}
          onClick={() => setSelectedPdfResponse(pdfResponse)}
      >
        <List.Icon style={{padding: "10px"}}
                   name={getIcon(pdfResponse.contentType)}
                   size="large"
                   verticalAlign="middle"
        />
        <List.Content>
          <ListContent>
            <List.Header as="h4">{pdfResponse.pdfName}</List.Header>
            <List.Description as="span">{pdfResponse.pdfSize}</List.Description>
          </ListContent>
          <DownloadSection>
            <Button
                as="a"
                href={getPdfUrl(pdfResponse)}
                download={pdfResponse.pdfName}
                icon
                style={btnStyle}
            >
              <Icon name="download" size="large"/>
            </Button>
          </DownloadSection>
        </List.Content>
      </StyledListItem>
  ));

  return (
      <StyledModal
          open={modalOpen}
          onClose={onCloseModal}
      >
        <StyledModalContent>
          <StyledSegmentGroup horizontal>
            <LeftSegment id={"leftSegment"}>
              {
                  type === FileTypes.PDF &&
                  <embed
                      src={fileDownloadUrl}
                      width={"100%"}
                      height={"100%"}
                  />
              }

              {
                  type === FileTypes.AUDIO &&
                  <>
                    <audio
                        className="audio"
                        controls
                        controlsList="nodownload"
                        src={filePreviewUrl}
                    />
                  </>
              }

              {
                  type === FileTypes.VIDEO &&
                  <>
                    <video
                        className="video"
                        controls
                        controlsList="nodownload"
                        src={filePreviewUrl}
                        autoPlay
                    />
                  </>
              }

              {
                  type === FileTypes.OTHER &&
                  <ImageComponent
                      mainSrc={mainImageSrc}
                      imageTitle={imageTitle()}
                      prevSrc={prevSrc()}
                      onMovePrevRequest={() => movePrevRequest()}
                      nextSrc={nextSrc()}
                      onMoveNextRequest={() => moveNextRequest()}
                      toolbarButtons={[
                        <StyledButton
                            href={getPdfUrl(selectedPdfResponse)}
                            download={selectedPdfResponse.pdfName}
                            icon
                            inverted
                            className="styled-button"
                        >
                          <Icon name="download" size="large"/>
                        </StyledButton>
                      ]}
                      segmentWidth={segmentWidth}
                      segmentHeight={segmentHeight}
                  />
              }
            </LeftSegment>
            <RightSegment>
              <div className="segment-body">
                <List divided relaxed selection>
                  {mappedPdfResponses}
                </List>
                {
                    errorMessage
                    && (
                        <StyledErrorMessage onDismiss={() => setErrorMessage(undefined)}>
                          {errorMessage}
                        </StyledErrorMessage>
                    )
                }

                {!draftMode && (
                    <div className="form-container">
                      <label htmlFor="textContent">
                        {getMessage("patientRecord.patientDocument.uploadPatientDocument.comment")}
                      </label>
                      <PatientDocumentComments
                          comments={comments}
                          currentUserDisplay={props.currentUserDisplay}
                          setAddingComment={setAddingComment}
                          setEditingComments={setEditingComments}
                          onChange={(comments: Partial<PatientMedicalDocumentCommentDtoExtended>[]) => {
                            setComments(comments);
                          }}
                      />
                    </div>
                )}
              </div>
              <div className="actions-row">
                {
                    showSetControlledButton &&
                    <Button compact
                            primary
                            type="button"
                            disabled={editingComments || addingComment}
                            onClick={() => setControlledStatus(documentId, patientId)}
                    >
                      {getMessage("button.setControlled")}
                    </Button>
                }
                <div className="divider"/>
                <Button compact
                        type="button"
                        onClick={onCloseModal}
                >
                  {getMessage("button.close")}
                </Button>
                {!draftMode && (
                    <Button compact
                            type="button"
                            disabled={!_.isNil(comments) && (addingComment || editingComments)}
                            onClick={() => savePatientMedicalDocument()}
                    >
                      {getMessage("button.saveAndClose")}
                    </Button>
                )}
              </div>
            </RightSegment>
          </StyledSegmentGroup>
        </StyledModalContent>
      </StyledModal>
  );
};

PatientMedicalDocumentPdfModal.defaultProps = {
  draftMode: false
}

export default withMessages(PatientMedicalDocumentPdfModal);
